import {
  compromissoTypeMessages,
  messages,
} from './components/personAgendaMessages';

const FILTER_CATEGORY_ORDER = [
  'leave',
  'hearing',
  'public_hearing',
  'event',
  'gifts_hospitalities',
  'meeting',
  'substituting',
  'travel_private_expenses',
  'travel_scdp',
];

const CATEGORY_ALIASES = {
  audiencia: 'hearing',
  'audiencia-publica': 'public_hearing',
  evento: 'event',
  reunion: 'meeting',
  reuniao: 'meeting',
  scdp: 'travel_scdp',
  viagem: 'travel_scdp',
  'custeio-privado': 'travel_private_expenses',
  travel_private_funding: 'travel_private_expenses',
  'presentes-hospitalidades': 'gifts_hospitalities',
};

export const normalizeCategoryToken = (value) => {
  const token =
    typeof value === 'string'
      ? value.trim().toLowerCase()
      : String(value || '');
  return CATEGORY_ALIASES[token] || token;
};

// Tipos com cor própria: travel_scdp, travel_private_expenses,
// gifts_hospitalities, hearing e public_hearing.
// Tipos sem cor própria usam a cor default: event, meeting, leave,
// substituting e qualquer token não mapeado.
const CATEGORY_COLORS = {
  travel_scdp: {
    backgroundColor: 'var(--green-cool-vivid-5, #e3f5e1)',
    borderColor: 'var(--green-cool-vivid-40, #00a91c)', // borda: --green-cool-vivid-40
    textColor: 'var(--gray-80, #333)',
  },
  travel_private_expenses: {
    backgroundColor: 'var(--blue-cool-vivid-5, #e1f3f8)',
    borderColor: 'var(--blue-cool-vivid-50, #0d7ea2)', // borda: --blue-cool-vivid-50
    textColor: 'var(--gray-80, #333)',
  },
  gifts_hospitalities: {
    backgroundColor: 'var(--yellow-5, #faf3d1)',
    borderColor: 'var(--yellow-20, #e6c74c)', // borda: --yellow-20
    textColor: 'var(--gray-80, #333)',
  },
  hearing: {
    backgroundColor: 'var(--red-cool-vivid-5, #fff2f5)',
    borderColor: 'var(--red-cool-vivid-50, #e41d3d)', // borda: --red-cool-vivid-50
    textColor: 'var(--gray-80, #333)',
  },
  public_hearing: {
    backgroundColor: 'var(--orange-vivid-5, #fdf5e6)',
    borderColor: 'var(--orange-vivid-30, #ff8c00)', // borda: --orange-vivid-30
    textColor: 'var(--gray-80, #333)',
  },

  // event: {
  //   backgroundColor: '#F7F2FF', // violet-vivid-05
  //   borderColor: '#8168B3', // violet-vivid-50
  //   textColor: 'var(--grey-80, #333)',
  // },
  // meeting: {
  //   backgroundColor: 'var(--blue-warm-vivid-10, #D4E5FF)',
  //   borderColor: 'var(--blue-warm-vivid-60, #155BCB)',
  //   textColor: 'var(--grey-80, #333)',
  // },

  // leave: {
  //   backgroundColor: 'var(--yellow-vivid-5, #FFF5C2)',
  //   borderColor: 'var(--yellow-vivid-20, #FFCD07)',
  //   textColor: 'var(--grey-80, #333)',
  // },

  // substituting: {
  //   backgroundColor: 'var(--blue-warm-vivid-10, #D4E5FF)',
  //   borderColor: 'var(--blue-warm-vivid-60, #155BCB)',
  //   textColor: 'var(--grey-80, #333)',
  // },
  default: {
    backgroundColor: 'var(--gray-5, #f0f0f0)',
    borderColor: 'var(--gray-50, #757575)', // borda: --gray-50
    textColor: 'var(--gray-80, #333)',
  },
};

export const getCategoryStyle = (category) => {
  const token = normalizeCategoryToken(category);
  return CATEGORY_COLORS[token] || CATEGORY_COLORS.default;
};

const getCompromissoType = (item) => {
  if (item?.compromisso_type && typeof item.compromisso_type === 'object') {
    return item.compromisso_type;
  }

  // Se compromisso_type é string, tenta retornar como token
  if (item?.compromisso_type && typeof item.compromisso_type === 'string') {
    return {
      token: item.compromisso_type,
      title: item.compromisso_type_title || item.compromisso_type,
    };
  }

  return null;
};

const humanizeCategoryToken = (value) => {
  if (!value || typeof value !== 'string') return '';

  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatMessageOrDefault = (intl, descriptor, values = {}) => {
  if (!descriptor) {
    return '';
  }

  if (intl?.formatMessage) {
    return intl.formatMessage(descriptor, values);
  }

  return (descriptor.defaultMessage || '').replace(
    /\{(\w+)\}/g,
    (match, key) => values[key] ?? match,
  );
};

const formatDateOrDefault = (intl, date, options, locale = 'en') => {
  if (intl?.formatDate) {
    return intl.formatDate(date, options);
  }

  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: options?.timeZone || 'UTC',
  }).format(date);
};

export const getCategoryLabel = (category, fallbackTitle = '', intl) => {
  const normalizedCategory = normalizeCategoryToken(category);
  const descriptor = compromissoTypeMessages[normalizedCategory];

  if (descriptor) {
    return formatMessageOrDefault(intl, descriptor);
  }

  const normalizedFallback = getNormalizedValue(fallbackTitle);

  if (
    normalizedFallback &&
    normalizedFallback.toLowerCase() !== normalizedCategory.toLowerCase()
  ) {
    return normalizedFallback;
  }

  return humanizeCategoryToken(normalizedCategory);
};

const getNormalizedValue = (value) => {
  if (Array.isArray(value)) {
    const firstString = value.find(
      (entry) => typeof entry === 'string' && entry.trim(),
    );
    return firstString || '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return '';
};

export const getConfiguredSource = (data) => {
  if (Array.isArray(data?.source)) return data.source[0];
  return data?.source;
};

export const resolveAgendaContext = ({ data, properties, source }) => {
  const sourceItem = source || getConfiguredSource(data);
  const contextPath = sourceItem?.['@id'] || properties?.['@id'] || '';
  const contextTitle = sourceItem?.Title || properties?.title || '';

  return {
    contextPath,
    contextTitle,
    sourceItem,
  };
};

export const buildMockAgendaResponse = (baseResponse, fallbackAgenda = {}) => {
  return {
    ...baseResponse,
    agenda: {
      ...baseResponse.agenda,
      title: fallbackAgenda.title || baseResponse.agenda.title,
      personTitle:
        fallbackAgenda.personTitle || baseResponse.agenda.personTitle,
      role: fallbackAgenda.role || baseResponse.agenda.role,
      downloadUrl:
        fallbackAgenda.downloadUrl || baseResponse.agenda.downloadUrl,
    },
  };
};

const normalizeBoolean = (value) => {
  return value === true || value === 'true' || value === 1;
};

export const normalizeAgendaItem = (item, intl) => {
  const compromissoType = getCompromissoType(item);
  const startValue = item?.start || item?.inicio || item?.start_date;
  const endValue = item?.end || item?.fim || item?.end_date;

  if (!startValue || !endValue) return null;

  // Mantém como string ISO, não converte para Date
  const start = startValue;
  const end = endValue;

  if (!start || !end) return null;

  // Extrai categoria com múltiplos fallbacks
  const category =
    item.category ||
    item.tipo ||
    compromissoType?.token ||
    item.compromisso_type_token ||
    item.compromisso_type ||
    'default';

  const normalizedCategory = normalizeCategoryToken(
    getNormalizedValue(category).toLowerCase(),
  );

  const categoryLabelFromData =
    getNormalizedValue(item.categoryTitle) ||
    getNormalizedValue(item.category_label) ||
    getNormalizedValue(item.Subject) ||
    getNormalizedValue(compromissoType?.title) ||
    getNormalizedValue(item.compromisso_type_title) ||
    getNormalizedValue(item.type_title);

  // Extrai título da categoria com múltiplos fallbacks
  const categoryTitle = getCategoryLabel(
    normalizedCategory,
    categoryLabelFromData,
    intl,
  );

  return {
    id: item.id || item.UID || item['@id'] || startValue,
    title:
      item.title ||
      item.Titulo ||
      formatMessageOrDefault(intl, messages.untitledEvent),
    description: item.description || item.Descricao || '',
    start,
    end,
    allDay: normalizeBoolean(item.allDay ?? item.whole_day),
    location: item.location || item.local || item.solicitante || '',
    category: normalizedCategory || 'default',
    categoryTitle,
    status: item.status || item.review_state || 'confirmed',
    link: item.url || item.href || item['@id'] || '',
    raw: item,
  };
};

export const resolveAgendaDownloadUrl = (content = {}) => {
  const objectButtons = content?.['@components']?.actions?.object_buttons || [];
  const exportAction = objectButtons.find(
    (action) => action.id === 'export_agenda',
  );

  return exportAction?.url || '';
};

export const normalizeAgendaResponse = (
  response,
  fallbackAgenda = {},
  intl,
) => {
  const agenda = {
    id: response?.agenda?.id || fallbackAgenda.id || '',
    title:
      response?.agenda?.title ||
      fallbackAgenda.title ||
      formatMessageOrDefault(intl, messages.defaultTitle),
    personTitle:
      response?.agenda?.personTitle ||
      fallbackAgenda.personTitle ||
      fallbackAgenda.title ||
      '',
    role: response?.agenda?.role || fallbackAgenda.role || '',
    downloadUrl:
      response?.agenda?.downloadUrl || fallbackAgenda.downloadUrl || '',
  };

  const items = (response?.items || [])
    .map((item) => normalizeAgendaItem(item, intl))
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = new Date(left.start).getTime();
      const rightTime = new Date(right.start).getTime();

      if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
      if (Number.isNaN(leftTime)) return 1;
      if (Number.isNaN(rightTime)) return -1;

      return leftTime - rightTime;
    });

  const categoryMap = new Map();

  FILTER_CATEGORY_ORDER.forEach((value) => {
    categoryMap.set(value, getCategoryLabel(value, '', intl));
  });

  (response?.filters?.categories || []).forEach((category) => {
    const value = normalizeCategoryToken(
      getNormalizedValue(category?.value).toLowerCase(),
    );
    if (!value) return;

    categoryMap.set(value, getCategoryLabel(value, category?.label, intl));
  });

  items.forEach((item) => {
    if (!categoryMap.has(item.category) || !categoryMap.get(item.category)) {
      categoryMap.set(
        item.category,
        getCategoryLabel(item.category, item.categoryTitle, intl),
      );
    }
  });

  const categories = Array.from(categoryMap.entries()).map(
    ([value, label]) => ({
      value,
      label,
    }),
  );

  return {
    agenda,
    items,
    categories,
    summary: response?.summary || { total: items.length },
  };
};

export const filterAgendaEvents = (
  items,
  { searchText, selectedCategories, startDate, endDate },
) => {
  const normalizedSearch = searchText?.trim().toLowerCase();

  return items.filter((item) => {
    const matchesSearch =
      !normalizedSearch ||
      [item.title, item.description, item.location, item.categoryTitle]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));

    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.includes(item.category);

    // Comparação simples de string (YYYY-MM-DD...)
    const getDateOnly = (dt) => (dt ? String(dt).slice(0, 10) : '');
    const matchesStartDate =
      !startDate || getDateOnly(item.start) >= getDateOnly(startDate);

    const matchesEndDate =
      !endDate || getDateOnly(item.start) <= getDateOnly(endDate);

    return (
      matchesSearch && matchesCategory && matchesStartDate && matchesEndDate
    );
  });
};

// toCalendarEvents e formatCurrentPeriodLabel foram movidas para utilsCalendar.js
// para evitar que moment-timezone (720 KB) entre no bundle principal via importações
// indiretas (ex: CompromissoView importa getCategoryStyle deste arquivo).
// Importar de './utilsCalendar' diretamente.

export const groupEventsByMonth = (items, intl, locale = 'pt-br') => {
  return items.reduce((accumulator, item) => {
    const key = String(item.start).slice(0, 7);
    const labelDate = new Date(`${key}-01T12:00:00Z`);
    const label = formatDateOrDefault(
      intl,
      labelDate,
      {
        month: 'long',
        year: 'numeric',
      },
      locale,
    );

    if (!accumulator[key]) {
      accumulator[key] = {
        key,
        label: label.charAt(0).toUpperCase() + label.slice(1),
        items: [],
      };
    }
    accumulator[key].items.push(item);
    return accumulator;
  }, {});
};
